import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { defaultRouteGuard, LoginComponent } from '@card-software/library';
import { MENU_ITEMS } from './app-menu';
import { BatchDetailsComponent } from './components/batch/batch-details/batch-details.component';
import { BatchesComponent } from './components/batch/batches/batches.component';
import { CurrentShiftComponent } from './components/current-shift/current-shift.component';
import { DashboardProductionCardComponent } from './components/dashboard-production-cards/dashboard-production-card/dashboard-production-card.component';
import { DyeingMachineDashboardComponent } from './components/dyeing/dyeing-dashboard/dyeing-machine-dashboard/dyeing-machine-dashboard.component';
import { DyeingMachineDetailsComponent } from './components/dyeing/dyeing-dashboard/dyeing-machine-details/dyeing-machine-details.component';
import { FinishingDashboardComponent } from './components/finishing/finishing-dashboard/finishing-dashboard.component';
import { FinishingDashboardDetailsComponent } from './components/finishing/finishing-dashboard-details/finishing-dashboard-details.component';
import { RollFinalQcComponent } from './components/finishing/roll-final-qc/roll-final-qc.component';
import { KnittingOrderComponent } from './components/knitting-order/knitting-order/knitting-order.component';
import { KnittingOrderBalanceComponent } from './components/knitting-order/knitting-order-balance/knitting-order-balance.component';
import { KnittingOrderDetailsComponent } from './components/knitting-order/knitting-order-details/knitting-order-details.component';
import { LaboratoryTestsComponent } from './components/laboratory-tests/laboratory-tests/laboratory-tests.component';
import { DyehouseDailyMetricComponent } from './components/main/dyehouse-daily-metric/dyehouse-daily-metric.component';
import { MachineAssignmentComponent } from './components/main/machine-assignment/machine-assignment.component';
import { PlanningDashboardComponent } from './components/planning/planning-dashboard/planning-dashboard.component';
import { ProductionCardsDetailsComponent } from './components/planning/product-order/production-cards-details/production-cards-details.component';
import { WorkOrdersComponent } from './components/planning/product-order/work-orders/work-orders.component';
import { CycleRecipeValuesComponent } from './components/production-card/cycle-recipe-values/cycle-recipe-values.component';
import { FinishingRouteDetailComponent } from './components/production-card/finishing-route-detail/finishing-route-detail.component';
import { IndividualProductionCardDetailsComponent } from './components/production-card/individual-production-card-details/individual-production-card-details.component';
import { ProductionCardBundlesComponent } from './components/production-card/production-card-bundles/production-card-bundles.component';
import { ReassignRollsComponent } from './components/production-card/reassign-rolls/reassign-rolls.component';
import { ReprocessCreationComponent } from './components/production-card/reprocess/reprocess-creation/reprocess-creation.component';
import { ProductionWorkOrderDetailsComponent } from './components/production-work-order/production-work-order-details/production-work-order-details.component';
import { ProductionWorkOrdersComponent } from './components/production-work-order/production-work-orders/production-work-orders.component';
import { ProductionReportingComponent } from './components/reporting/production-reporting.component';
import { RollSplitComponent } from './components/roll-split/roll-split.component';
import { KnittingDashboardComponent } from './components/stations/knitting-dashboard/knitting-dashboard.component';
import { MachinesAssignmentComponent } from './components/stations/machines-assignment/machines-assignment.component';
import { StationsComponent } from './components/stations/stations/stations.component';
import { WeightStationComponent } from './components/stations/weight/weight-station/weight-station.component';
import { WeightStationDetailComponent } from './components/stations/weight/weight-station-detail/weight-station-detail.component';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { AuthenticationGuard } from './guards/authentication.guard';
import { RoleGuard } from './guards/Role.guard';
import {
  BatchingRoles,
  DailyProductionProcessRoles,
  DyehouseDailyMetricRoles,
  DyeingRoles,
  FinishingRoles,
  KnittingOrderRoles,
  MachineAssignmentRoles,
  MachineDashboardRoles,
  PlanningDashboardRoles,
  ProductionBatchRoles,
  ProductionCardRoles,
  ProductionDetailProcessRoles,
  ProductionReportsRoles,
  ProductionWorkOrderRoles,
  ReprocessOrdersRoles,
  ShiftsRoles,
  StationsRoles,
} from './models/roles/roles-group';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [AuthenticationGuard, defaultRouteGuard(MENU_ITEMS, 'Production')],
    component: UnauthorizedComponent,
  },
  {
    path: 'planning-dashboard',
    component: PlanningDashboardComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Planning' }, { label: 'Breadcrumb_Planning_Dashboard' }],
      roles: [PlanningDashboardRoles.PlanningDashboardViewer, PlanningDashboardRoles.PlanningDashboardAdmin],
    },
  },
  {
    path: 'work-orders-for-production-cards',
    component: WorkOrdersComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Planning' }, { label: 'Production_Cards' }],
      roles: [ProductionCardRoles.ProductionCardsEditor, ProductionCardRoles.ProductionCardsViewer],
    },
  },
  {
    path: 'production-cards-details',
    component: ProductionCardsDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Planning' },
        { label: 'Production_Cards', route: '/work-orders-for-production-cards' },
        { label: 'Breadcrumb_Production_Cards_Details', awaitCurrent: true },
      ],
      roles: [ProductionCardRoles.ProductionCardsEditor, ProductionCardRoles.ProductionCardsViewer],
    },
  },
  {
    path: 'knitting-order-details',
    component: KnittingOrderDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Planning' },
        { label: 'knitting_orders', route: '/knitting-orders' },
        { label: 'knitting_order_details', awaitCurrent: true },
      ],
      roles: [KnittingOrderRoles.KnittingOrderAllEditor, KnittingOrderRoles.KnittingOrderViewer],
    },
  },
  {
    path: 'knitting-orders',
    component: KnittingOrderComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [KnittingOrderRoles.KnittingOrderAllEditor, KnittingOrderRoles.KnittingOrderViewer],
      breadcrumb: [{ label: 'Planning' }, { label: 'knitting_orders' }],
    },
  },
  {
    path: 'knitting-order-yarn-balance',
    component: KnittingOrderBalanceComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Planning' }, { label: 'Breadcrumb_Knitting_Yarn_Balance' }],
      roles: [KnittingOrderRoles.KnittingOrderViewer, KnittingOrderRoles.KnittingOrderAllEditor],
    },
  },
  {
    path: 'stations',
    component: StationsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Breadcrumb_Knitting' }, { label: 'Stations' }],
      roles: [StationsRoles.WeightStationsEditor, StationsRoles.WeightStationsViewer],
    },
  },
  {
    path: 'shifts',
    component: CurrentShiftComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Breadcrumb_Knitting' }, { label: 'Breadcrumb_Shifts' }],
      roles: [ShiftsRoles.ShiftsViewer, ShiftsRoles.ShiftsAdmin],
    },
  },
  {
    path: 'stations/weight-station',
    component: WeightStationComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Breadcrumb_Knitting' },
        { label: 'Stations', route: '/stations' },
        { label: 'WeightStation', awaitCurrent: true },
      ],
      roles: [StationsRoles.WeightStationsEditor, StationsRoles.WeightStationsViewer],
    },
  },
  {
    path: 'stations/knitting-dashboard',
    component: KnittingDashboardComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Breadcrumb_Knitting' }, { label: 'Breadcrumb_Machine_Dashboard' }],
      roles: [MachineDashboardRoles.MachineDashboardViewer, MachineDashboardRoles.MachineDashboardEditor],
    },
  },
  {
    path: 'stations/machines-assignment',
    component: MachinesAssignmentComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Breadcrumb_Knitting' },
        { label: 'Stations', route: '/stations' },
        { label: 'Breadcrumb_Machines_Assignment' },
      ],
      roles: [MachineDashboardRoles.MachineDashboardViewer, MachineDashboardRoles.MachineDashboardEditor],
    },
  },
  {
    path: 'weight-station-detail',
    component: WeightStationDetailComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [StationsRoles.WeightStationsEditor, StationsRoles.WeightStationsViewer],
    },
  },
  {
    path: 'dyeing/dashboard',
    component: DyeingMachineDashboardComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Dyeing' }, { label: 'Breadcrumb_Dashboard' }],
      roles: [DyeingRoles.DyeingViewer, DyeingRoles.DyeingAdmin],
    },
  },
  {
    path: 'dyeing/dashboard/:assetId',
    component: DyeingMachineDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Dyeing' },
        { label: 'Breadcrumb_Dashboard', route: '/dyeing/dashboard' },
        { label: 'Breadcrumb_Machine_Details', awaitCurrent: true },
      ],
      roles: [DyeingRoles.DyeingViewer, DyeingRoles.DyeingAdmin],
    },
  },
  {
    path: 'finishing/dashboard',
    component: FinishingDashboardComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Breadcrumb_Finishing' }, { label: 'Breadcrumb_Stations_Dashboard' }],
      roles: [FinishingRoles.FinishingViewer, FinishingRoles.FinishingAdmin],
    },
  },
  {
    path: 'finishing/dashboard/:assetId',
    component: FinishingDashboardDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [FinishingRoles.FinishingViewer, FinishingRoles.FinishingAdmin],
    },
  },
  {
    path: 'finishing/laboratory-tests',
    component: LaboratoryTestsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [FinishingRoles.FinishingViewer, FinishingRoles.FinishingAdmin],
    },
  },
  {
    path: 'finishing/roll-final-qc',
    component: RollFinalQcComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Breadcrumb_Finishing' }, { label: 'Breadcrumb_Roll_Final_QC' }],
      roles: [FinishingRoles.FinishingViewer, FinishingRoles.FinishingAdmin],
    },
  },
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'roll-split',
    component: RollSplitComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Breadcrumb_Knitting' }, { label: 'Breadcrumb_Roll_Split' }],
      roles: [ProductionCardRoles.ProductionCardsEditor, ProductionCardRoles.ProductionCardsViewer],
    },
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  {
    path: 'production-card/:productionCardNumber/details',
    component: IndividualProductionCardDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [
        ProductionCardRoles.ProductionMainViewer,
        ProductionCardRoles.ProductionCardsEditor,
        ProductionCardRoles.ProductionCardsViewer,
      ],
    },
  },
  {
    path: 'production-card/:productionCardNumber/bundles',
    component: ProductionCardBundlesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [BatchingRoles.BatchingViewer, BatchingRoles.BatchingAdmin],
    },
  },
  {
    path: 'production-card/:productionCardNumber/finishing-route/:finishingRouteNumber',
    component: FinishingRouteDetailComponent,

    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [FinishingRoles.FinishingViewer, FinishingRoles.FinishingAdmin],
    },
  },
  {
    path: 'reprocess',
    component: ReprocessCreationComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [ReprocessOrdersRoles.ReprocessOrdersAdmin],
      breadcrumb: [{ label: 'Breadcrumb_Reprocess' }, { label: 'Breadcrumb_Reprocess_PCs' }],
    },
  },
  {
    path: 'reassign-rolls',
    component: ReassignRollsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Breadcrumb_Knitting' }, { label: 'Breadcrumb_Reassign_Rolls' }],
      roles: [ProductionCardRoles.ProductionCardsEditor, ProductionCardRoles.ProductionCardsViewer],
    },
  },
  {
    path: 'production/cycles/:cycleId/recipe-values',
    component: CycleRecipeValuesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [DyeingRoles.DyeingViewer, DyeingRoles.DyeingAdmin],
    },
  },
  {
    path: 'production-work-orders',
    component: ProductionWorkOrdersComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Breadcrumb_Production' }, { label: 'Breadcrumb_Production_Work_Orders' }],
      roles: [ProductionWorkOrderRoles.ProductionWorkOrderEditor, ProductionWorkOrderRoles.ProductionWorkOrderViewer],
    },
  },
  {
    path: 'production-work-order-details',
    component: ProductionWorkOrderDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      roles: [ProductionWorkOrderRoles.ProductionWorkOrderEditor, ProductionWorkOrderRoles.ProductionWorkOrderViewer],
    },
  },
  {
    path: 'batches',
    component: BatchesComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Planning' }, { label: 'Breadcrumb_Batches' }],
      roles: [ProductionBatchRoles.ProductionBatchEditor, ProductionBatchRoles.ProductionBatchViewer],
    },
  },
  {
    path: 'batch-details',
    component: BatchDetailsComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [
        { label: 'Planning' },
        { label: 'Breadcrumb_Batches', route: '/batches' },
        { label: 'Breadcrumb_Batch_Details', awaitCurrent: true },
      ],
      roles: [ProductionBatchRoles.ProductionBatchEditor, ProductionBatchRoles.ProductionBatchViewer],
    },
  },
  {
    path: 'main/dashboard-production-cards',
    component: DashboardProductionCardComponent,
    pathMatch: 'full',

    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Breadcrumb_Main' }, { label: 'Breadcrumb_Production' }],
      roles: [
        ProductionCardRoles.ProductionMainViewer,
        ProductionCardRoles.ProductionCardsEditor,
        ProductionCardRoles.ProductionCardsViewer,
      ],
    },
  },
  {
    path: 'main/machine-assignment',
    component: MachineAssignmentComponent,
    pathMatch: 'full',
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Breadcrumb_Main' }, { label: 'Breadcrumb_Machine_Assignment' }],
      roles: [MachineAssignmentRoles.MachineAssignmentViewer, MachineAssignmentRoles.MachineAssignmentAdmin],
    },
  },
  {
    path: 'main/daily-consumptions',
    component: DyehouseDailyMetricComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Breadcrumb_Main' }, { label: 'Breadcrumb_Daily_Consumptions' }],
      roles: [DyehouseDailyMetricRoles.DyehouseDailyMetricViewer, DyehouseDailyMetricRoles.DyehouseDailyMetricAdmin],
    },
  },
  {
    path: 'reporting',
    component: ProductionReportingComponent,
    canActivate: [AuthenticationGuard, RoleGuard],
    data: {
      breadcrumb: [{ label: 'Breadcrumb_Reporting' }, { label: 'Breadcrumb_Production_Reports' }],
      roles: [
        ProductionReportsRoles.ProductionReportsViewer,
        ProductionReportsRoles.ProductionReportsAdmin,
        ...Object.values(DailyProductionProcessRoles),
        ...Object.values(ProductionDetailProcessRoles),
      ],
    },
  },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'full',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
